import {
  type AdminNavSearchEntry,
  getAdminNavSearchEntries,
} from "@/routing/adminNavModelQueries";
import {
  ADMIN_ROUTES,
  type AdminRouteAccessArgs,
  evaluateAdminRouteAccess,
  findAdminRouteByPathname,
} from "@/routing/adminRoutes";
import type { JsonObject } from "@/types/adminV2";
import { normalizeAdminFrontendPath } from "@/utils/navigation/normalizeAdminFrontendPath";

export interface SearchRouteResolverArgs {
  groupType: string;
  itemId: string;
  metadata: JsonObject | null;
  routeAccess: AdminRouteAccessArgs;
}

export interface SearchDestinationResult {
  id: string;
  label: string;
  path: string;
  description: string;
}

interface SearchStatusInput {
  query: string;
  isFetching: boolean;
  isError: boolean;
  groupCount: number;
}

export type SearchStatus =
  "startTyping" | "searching" | "error" | "empty" | "results";

const GROUP_TYPE_FALLBACK_ROUTES: Partial<
  Record<string, (itemId: string) => string>
> = {
  staff_user: () => "/settings/team-permissions",
  crm_person: (itemId: string) => `/crm/contacts/${itemId}`,
  crm_business: (itemId: string) => `/crm/businesses/${itemId}`,
  crm_firm: (itemId: string) => `/crm/firms/${itemId}`,
  support_ticket: (itemId: string) =>
    `/issue-reports?category=support_ticket&q=${encodeURIComponent(itemId)}`,
  help_article: (itemId: string) => `/support/help-centre/articles/${itemId}`,
  billing_offer: () => "/revenue/billing",
  billing_coupon: () => "/revenue/coupons",
  billing_affiliate: () => "/revenue/affiliates",
  sales_deal: () => "/sales",
  growth_form: (itemId: string) => `/forms/${itemId}`,
  growth_tag: () => "/audiences/tags",
  growth_segment: () => "/audiences",
};

const SCREENSHOT_UNSAFE_SEARCH_DESTINATION_PATHS = new Set([
  "/spoof",
  "/advanced-spoof",
  "/spoof-sessions",
  "/spoof-launcher",
]);

const SEARCH_GROUP_LABELS: Partial<Record<string, string>> = {
  billing_affiliate: "Affiliates",
  billing_coupon: "Coupons",
  billing_offer: "Billing offers",
  crm_business: "Organisations",
  crm_firm: "Partners",
  crm_person: "Contacts",
  growth_form: "Forms",
  growth_segment: "Audiences",
  growth_tag: "Audience tags",
  help_article: "Help articles",
  sales_deal: "Sales deals",
  staff_user: "Staff users",
  support_ticket: "Issue reports",
};

const SEARCH_DESTINATION_ALIASES: Partial<Record<string, string[]>> = {
  system_logs: ["logs", "audit logs", "platform logs", "product logs"],
  transcript_intake: ["transcripts", "app feedback", "call transcripts"],
};

const readRoutePathFromMetadata = (
  metadata: JsonObject | null,
): string | null => {
  if (!metadata) {
    return null;
  }

  const routePath = metadata.routePath;
  if (typeof routePath === "string" && routePath.startsWith("/")) {
    return normalizeAdminFrontendPath(routePath);
  }

  return null;
};

const resolveSearchRouteCandidatePath = (
  args: Pick<SearchRouteResolverArgs, "groupType" | "itemId" | "metadata">,
): string | null => {
  const supportTicketIssueReportsPath = `/issue-reports?category=support_ticket&q=${encodeURIComponent(
    args.itemId,
  )}`;
  const metadataRoutePath = readRoutePathFromMetadata(args.metadata);
  if (metadataRoutePath) {
    // July 3 Issue Reports v2 retired `/support/tickets*`; search results
    // can still carry old stored metadata, so route them into Issue Reports.
    if (
      args.groupType === "support_ticket" &&
      (metadataRoutePath === "/support" ||
        metadataRoutePath === "/support/tickets" ||
        metadataRoutePath.startsWith("/support/tickets/"))
    ) {
      return supportTicketIssueReportsPath;
    }

    return metadataRoutePath;
  }

  const fallbackResolver = GROUP_TYPE_FALLBACK_ROUTES[args.groupType];
  return fallbackResolver ? fallbackResolver(args.itemId) : null;
};

const normalizeSearchText = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[/_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const buildDestinationSearchText = (
  entry: AdminNavSearchEntry,
  tabRouteIds: ReadonlySet<string>,
): string => {
  const route = ADMIN_ROUTES.find((item) => item.id === entry.routeId);
  // A page opens on its first tab; that tab, not the page, answers to the
  // tab route's own label (searching "System logs" finds Logs › System logs).
  const isTabbedPage =
    !entry.id.includes(".") && tabRouteIds.has(entry.routeId);
  const values = [
    entry.label,
    entry.location,
    entry.path,
    isTabbedPage ? undefined : route?.label,
    ...(SEARCH_DESTINATION_ALIASES[entry.routeId] ?? []),
  ].filter((value): value is string => Boolean(value));

  return normalizeSearchText(values.join(" "));
};

/**
 * Page and tab destinations from the navigation map (Nav PRD §4): every
 * sidebar page and tab plus search-only pages, never hidden routes.
 */
export const getSearchDestinationResults = ({
  query,
  routeAccess,
  limit = 8,
}: {
  query: string;
  routeAccess: AdminRouteAccessArgs;
  limit?: number;
}): SearchDestinationResult[] => {
  const normalizedQuery = normalizeSearchText(query);
  if (normalizedQuery.length < 2) {
    return [];
  }

  const seenPaths = new Set<string>();
  const entries = getAdminNavSearchEntries(routeAccess);
  const tabRouteIds = new Set(
    entries
      .filter((entry) => entry.id.includes("."))
      .map((entry) => entry.routeId),
  );

  return entries
    .filter(
      (entry) => !SCREENSHOT_UNSAFE_SEARCH_DESTINATION_PATHS.has(entry.path),
    )
    .filter((entry) =>
      buildDestinationSearchText(entry, tabRouteIds).includes(normalizedQuery),
    )
    .flatMap((entry) => {
      const path = normalizeAdminFrontendPath(entry.path);
      if (!path || seenPaths.has(`${path}::${entry.label}`)) {
        return [];
      }

      seenPaths.add(`${path}::${entry.label}`);
      return [
        {
          id: entry.id,
          label: entry.label,
          path,
          description: entry.location,
        },
      ];
    })
    .slice(0, limit);
};

export const resolveSearchRoutePath = (
  args: SearchRouteResolverArgs,
): string | null => {
  const candidatePath = resolveSearchRouteCandidatePath(args);
  if (!candidatePath) {
    return null;
  }

  const [candidatePathname] = candidatePath.split(/[?#]/, 1);
  if (SCREENSHOT_UNSAFE_SEARCH_DESTINATION_PATHS.has(candidatePathname)) {
    return null;
  }

  const route = findAdminRouteByPathname(candidatePathname);
  if (!route) {
    return null;
  }

  return evaluateAdminRouteAccess(route, args.routeAccess).canAccess
    ? candidatePath
    : null;
};

export const formatSearchGroupLabel = (groupType: string): string => {
  const knownLabel = SEARCH_GROUP_LABELS[groupType];
  if (knownLabel) {
    return knownLabel;
  }

  const normalized = groupType.replace(/[-_]+/g, " ").trim();
  return normalized.length > 0
    ? normalized.charAt(0).toUpperCase() + normalized.slice(1)
    : "Records";
};

export const getSearchStatus = (input: SearchStatusInput): SearchStatus => {
  if (input.query.trim().length < 2) {
    return "startTyping";
  }

  if (input.isFetching) {
    return "searching";
  }

  if (input.isError) {
    return "error";
  }

  if (input.groupCount === 0) {
    return "empty";
  }

  return "results";
};
